import { combineReducers, Reducer } from 'redux'
import { SensibleNumber } from '../common/SensibleNumber'
import { cashoutReducer } from './cashier/CashoutReducer'
import { sddStatusReducer } from './cashier/SddStatusReducer'
import { phoneVerificationReducer } from './cashier/PhoneVerificationReducer'
import { PaymentProcessingStatus } from './cashier/PurchaseActions'
import { purchaseReducer } from './cashier/PurchaseReducer'
import { userReducer } from './cashier/UserReducer'
import { clockReducer } from './ClockReducer'
import { jackpotPrizeHistoryReducer } from './jackpotSitAndGo/jackpotPrizeHistoryReducer'
import { jackpotSitAndGoEntriesReducer } from './jackpotSitAndGo/JackpotSitAndGoEntriesReducer'
import { accountReducer } from './player/AccountReducer'
import { AccountState } from '../state/AccountState'
import { achievementReducer, AchievementState } from './player/AchievementsReducer'
import { bonusReducer, BonusState } from './player/BonusReducer'
import { historyReducer } from './player/HandHistoryReducer'
import { itemsReducer, ItemsState } from './player/ItemsReducer'
import { PlayerProfileState, profileReducer } from './player/ProfileReducer'
import { responsibleGamingReducer, ResponsibleGamingState } from './player/ResponsibleGamingReducer'
import { transactionHistoryReducer, TransactionHistoryState } from './player/TransactionHistoryReducer'
import { ApplicationState } from './Store'
import { PlayerItemView } from '../communication/PlayerApiProtocol.generated'
import { playerItemsReducer } from './player-items/PlayerItemsReducer'
import { PhoneVerificationStatus } from '../services/PhoneVerificationService'
import { gameServerReducer, GameServerStatus } from './game-server/GameServerReducer'
import { casinoGamesReducer } from './casino-games/CasinoGamesReducer'
import { recentlyPlayedReducer } from './cashier/RecentlyPlayedReducer'
import { triggerReducer } from './triggers/TriggerReducer'
import { socialLiveCasinoStatsReducer } from './social-live-casino/LiveStatsReducer'
import { brazeReducer } from './braze/BrazeReducer'
import { viewsReducer } from './views/ViewsReducer'
import { jsngGamesReducer } from './jsng-games/JsngGamesReducer'
import { playerGamesReducer } from './player-games/PlayerGameReducer'

export interface PlayerState {
    profile: PlayerProfileState;
    achievementState: AchievementState;
    itemState: ItemsState;
    account: AccountState;
    bonus: BonusState;
    transactionState: TransactionHistoryState;
    responsibleGaming: ResponsibleGamingState;
}

export const playerReducer: Reducer<PlayerState> = combineReducers<PlayerState>({
	profile : profileReducer,
	achievementState : achievementReducer,
	itemState : itemsReducer,
	account : accountReducer,
	bonus : bonusReducer,
	transactionState : transactionHistoryReducer,
	responsibleGaming : responsibleGamingReducer,
})

export const appReducer: Reducer<ApplicationState> = combineReducers<ApplicationState>({
	braze: brazeReducer,
	cashout: cashoutReducer,
	clock: clockReducer,
	history: historyReducer,
	jsngJackpots: jackpotPrizeHistoryReducer,
	jsngEntries: jackpotSitAndGoEntriesReducer,
	player: playerReducer,
	purchase: purchaseReducer,
	sddStatus: sddStatusReducer,
	user: userReducer,
	phoneVerification: phoneVerificationReducer,
	playerItems: playerItemsReducer,
	gameServer: gameServerReducer,
	casinoGames: casinoGamesReducer,
	jsngGames: jsngGamesReducer,
	recentlyPlayedGameItems: recentlyPlayedReducer,
	triggers: triggerReducer,
	socialLiveCasinoStats: socialLiveCasinoStatsReducer,
	views: viewsReducer,
	playerGames: playerGamesReducer,
})

export enum CashierModalTab {
    HIDDEN, DEPOSIT, CASHOUT, TRANSACTIONS, SUPPORT, PLAY_FREE, BONUS_CODE,
}

export interface CashoutState {
    amount: SensibleNumber;
    userCashoutInfo?: UserCashoutInfo;
    submitted: boolean;
    submissionSuccess: boolean;
    submissionError: boolean;
    purchaseCountersLoading: boolean;
    purchaseCounters: { [key: string]: string };
}

export interface PurchaseState {
    paymentsUrl?: string;
    paymentsPortalOpen?: boolean;
    bundle?: Bundle;
    provider?: PaymentProvider;
    providerToken?: string;
    submitted: boolean;
    submissionError?: string;
    processingStatus?: PaymentProcessingState;
    sessionId?: string;
    purchaseLimits: PurchaseLimitPeriod[];
    bundleOptions: {
        bundles: Bundle[];
        offers: Bundle[];
        bonus: Bundle[];
    };
    kycLimitApplicable: boolean;
    loadingLimits: boolean;
    loadingBundles: boolean;
    loadingBundleItems: boolean;
    loading: boolean;
    bonusCode?: string;
    bundleItems: { [nameId: string]: PlayerItemView };
    hasMadePurchases: boolean;
    source?: string;
}

export interface PaymentProcessingState {
    invoiceId: string;
    status: PaymentProcessingStatus;
    error?: string;
    firstPurchase?: boolean;
    provider?: string;
}

export interface UserState {
    userInfo?: UserInfo;
    balance?: Balance;
    kycUpdateRequestInProgress?: boolean;
    addressFetchRequestInProgress?: boolean;
    personalInfoFetchRequestInProgress?: boolean;
    loginCompleted?: boolean;
    addressInfo?: AddressInfo;
    personalInfo?: PersonalInfo;
    verificationInfo?: VerificationInfo;
    blockPurchaseUntilRefresh?: boolean;
}

export interface PhoneVerificationState {
    phoneVerificationRequired: boolean;
    submitPhoneNumberInProgress: boolean;
    validPhoneNumberSubmitted: boolean;
    submitVerificationCodeInProgress: boolean;
    validVerificationCodeSubmitted: boolean;
    internationalPhoneNumber?: InternationalPhoneNumber;
    secondsToExpire?: number;
    errorMsg?: string;
    submitPurchase: boolean;
    status: PhoneVerificationStatus;
}


export interface GameServerState {
    status: GameServerStatus
}
